<template lang="pug">
.excel-import-modal
  b-modal(id="excelimportmodal", :title="title", size="xl", ok-variant="primary", ref="excelimportmodal", 
    :ok-title="getOkButtonTitle", 
    :cancel-title="getCancelButtonTitle", 
    @ok="handleNext", 
    @cancel="handleBack",
    @hidden="resetModal",
    :busy="isImporting")
    
    // Step 1: File Upload
    div(v-if="currentStep === 0")
      .form-group
        label(for="excelFile").small Excel-Datei auswählen
        .custom-file-upload
          input.form-control(
            type="file", 
            accept=".xlsx,.xls", 
            @change="handleFileUpload", 
            ref="fileInput",
            :disabled="isUploading"
          )
          .upload-overlay(v-if="isUploading")
            b-spinner(variant="primary")
            span.ms-2 Datei wird hochgeladen...
      
      .mt-3(v-if="previewData.length > 0")
        .form-group.mb-3
          label.small Zeilen nach der Kopfzeile überspringen
          .d-flex.align-items-center
            input.form-control.form-control-sm(
              type="number",
              v-model.number="skipRows",
              min="0",
              style="max-width: 100px"
            )
            .small.text-muted.ms-2 Anzahl der Zeilen, die nach der Kopfzeile übersprungen werden sollen

        h5 Vorschau der Daten
        .table-responsive
          table.table.table-sm.table-striped
            thead
              tr
                th(v-for="(header, index) in headers" :key="index") {{ header }}
            tbody
              tr(v-for="(row, rowIndex) in previewData.slice(0, 20)" :key="rowIndex")
                td(v-for="(cell, cellIndex) in row" :key="cellIndex") {{ cell }}

    // Step 2: Column Mapping
    div(v-if="currentStep === 1")
      .form-group
        h5 Spaltenzuordnung
        p.small.text-muted Ordnen Sie die Excel-Spalten den Dokumentfeldern zu
        .table-responsive
          table.table.table-sm.table-striped
            thead
              tr
                th Excel-Spalte
                th Zieldokumentfeld
            tbody
              tr(v-for="(header, index) in headers" :key="index")
                td {{ header }}
                td
                  select.form-control.form-control-sm(v-model="mapping[index].targetField")
                    option(value="") Keine Zuordnung
                    option(value="filename") Dateiname (nur für Matching)
                    option(value="selected_class") DCC 
                    option(value="selected_kks") KKS
                    option(value="extracted_info") Extrahierte Information
                  template(v-if="mapping[index].targetField === 'selected_kks'")
                    .small.text-muted
                      | Mehrere KKS-Werte werden mit Komma getrennt zusammengeführt
                    .small.text-info.mt-1
                      | Hinweis: Sie können mehrere Excel-Spalten zu KKS zuordnen. Alle Werte werden kombiniert.
                  template(v-if="mapping[index].targetField === 'extracted_info'")
                    input.form-control.form-control-sm(
                      v-model="mapping[index].extractedInfoKey",
                      placeholder="Schlüssel für extrahierte Information"
                    )
        
        .form-group.mt-4
          h5 Verhalten für Zuordnunge
          p.small.text-muted Wählen Sie, wie mit bestehenden Zuordnungen umgegangen werden soll
          select.form-control(v-model="assignmentBehavior")
            option(value="create") Erstelle neue Zuordnung und behalte existierende
            option(value="update") Füge KKS allen existierenden Zuordnungen zu
            option(value="replace") Lösche alle Zuordnungen und erstelle eine neue Zuordnung
            
            

    // Step 3: Import Results
    div(v-if="currentStep === 2")
      .form-group
        h5 Import-Zusammenfassung
        p.small.text-muted Überprüfen Sie die Importdetails
        .table-responsive
          table.table.table-sm.table-striped
            thead
              tr
                th Excel-Spalte
                th Zieldokumentfeld
                th Beispielwert
            tbody
              tr(v-for="(header, index) in headers" :key="index" v-if="mapping[index].targetField")
                td {{ header }}
                td 
                  span {{ mapping[index].targetField }}
                  span(v-if="mapping[index].targetField === 'extracted_info'") 
                    | ({{ mapping[index].extractedInfoKey }})
                td {{ previewData[0] ? previewData[0][index] : '' }}

      .mt-3
        b-alert(variant="info" show)
          | Es werden {{ previewData.length }} Datensätze importiert.
          br
          | Für jeden Datensatz wird nach dem Dateinamen gesucht und die entsprechenden Felder aktualisiert.
          br
          | Verhalten bei Zuordnungen: 
          span(v-if="assignmentBehavior === 'replace'") 
            strong Lösche alle Zuordnungen und erstelle eine neue Zuordnung
          span(v-if="assignmentBehavior === 'update'") 
            strong Füge KKS allen existierenden Zuordnungen zu
          span(v-if="assignmentBehavior === 'create'") 
            strong Erstelle neue Zuordnung und behalte existierende Zuordnungen

    // Add new step for import results
    div(v-if="currentStep === 3")
      .form-group
        h5 Import-Ergebnisse
        .mb-3
          b-alert(variant="success" show)
            | Erfolgreich importierte Datensätze: {{ importResults.successful }}
          b-alert(v-if="importResults.failed > 0" variant="warning" show)
            | Fehlgeschlagene Datensätze: {{ importResults.failed }}
          
          b-alert(variant="info" show)
            | Angewendetes Verhalten bei Zuordnungen: 
            strong(v-if="assignmentBehavior === 'replace'") Lösche alle Zuordnungen und erstelle eine neue Zuordnung
            strong(v-if="assignmentBehavior === 'update'") Füge KKS allen existierenden Zuordnungen zu
            strong(v-if="assignmentBehavior === 'create'") Erstelle neue Zuordnung und behalte existierende Zuordnungen
        
        .table-responsive(v-if="importResults.details.length")
          table.table.table-sm.table-striped
            thead
              tr
                th Dateiname
                th Status
                th Details
            tbody
              tr(v-for="(result, index) in importResults.details" :key="index")
                td {{ result.filename }}
                td
                  b-badge(
                    :variant="result.status === 'success' ? 'success' : 'danger'"
                  ) {{ result.status }}
                td
                  template(v-if="result.status === 'failed'")
                    span.text-danger {{ result.error }}
                  template(v-else)
                    .small
                      div(v-if="result.updates.assignments")
                        strong DCC/KKS: 
                        span DCC: {{ result.updates.assignments[0].selected_class.join(', ') }}
                        br
                        span KKS: {{ result.updates.assignments[0].selected_kks.join(', ') }}
                      div(v-if="result.updates.extracted_information")
                        strong Extrahierte Information:
                        ul.mb-0
                          li(v-for="(info, i) in result.updates.extracted_information" :key="i")
                            | {{ info.key }}: {{ info.value }}

</template>

<script>
import Vue from 'vue'

export default {
  name: 'ExcelImportModal',
  props: {
    projectId: {
      type: String,
      required: true,
      validator: function(value) {
        return value && value.length > 0;
      }
    }
  },

  data() {
    return {
      title: "Excel Import",
      currentStep: 0,
      excelFile: null,
      headers: [],
      previewData: [],
      mapping: [],
      importResults: null,
      zieldokumentfeld: "XLS", // Default value for ZIELDOKUMENTFELD
      skipRows: 0, // Add skipRows to data
      rawData: [], // Store the complete raw data
      isUploading: false, // Add loading state
      assignmentBehavior: "create", // Add assignmentBehavior to data
      isImporting: false // Add loading state for import button
    }
  },

  computed: {
    activeMappings() {
      return this.mapping.filter(m => m.targetField);
    },
    getOkButtonTitle() {
      switch(this.currentStep) {
        case 2:
          return 'Importieren';
        case 3:
          return 'Schließen';
        default:
          return 'Weiter';
      }
    },
    getCancelButtonTitle() {
      switch(this.currentStep) {
        case 0:
          return 'Abbrechen';
        case 3:
          return 'Neuer Import';
        default:
          return 'Zurück';
      }
    }
  },

  watch: {
    skipRows(newVal) {
      // When skipRows changes, update previewData from rawData
      // Keep the original headers (first row) and skip N rows after it
      if (this.rawData.length > 0) {
        this.headers = this.rawData[0] || []; // Always use first row as headers
        this.previewData = this.rawData.slice(newVal + 1); // Skip N rows after headers
      }
    }
  },

  methods: {
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      if (!this.projectId) {
        this.$bvToast.toast("Kein Projekt ausgewählt", {
          title: "Fehler",
          variant: "danger",
          autoHideDelay: 3000,
        });
        return;
      }

      this.excelFile = file;
      this.isUploading = true;

      const formData = new FormData();
      formData.append('file', file);

      // Send file to backend for parsing
      this.axios.post(
        `${this.base_url}/projects/${this.projectId}/excel-import/parse`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      ).then(response => {
        if (response.data && response.data.headers && response.data.rows) {
          // Store the complete data
          this.rawData = [response.data.headers, ...response.data.rows];
          // Always use first row as headers
          this.headers = this.rawData[0] || [];
          // Apply current skipRows after headers
          this.previewData = this.rawData.slice(this.skipRows + 1);
          
          // Initialize mapping
          this.mapping = this.headers.map(() => ({
            targetField: ''
          }));
        } else {
          throw new Error("Ungültiges Antwortformat vom Server");
        }
      }).catch(error => {
        console.error("Error parsing Excel file:", error);
        const errorMessage = error.response && error.response.data && error.response.data.detail 
          ? error.response.data.detail 
          : error.message;
        this.$bvToast.toast("Fehler beim Lesen der Excel-Datei: " + errorMessage, {
          title: "Fehler",
          variant: "danger",
          autoHideDelay: 3000,
        });
      }).finally(() => {
        this.isUploading = false;
      });
    },

    handleNext(ev) {
      if (this.currentStep === 0) {
        if (!this.excelFile) {
          alert("Bitte wählen Sie eine Excel-Datei aus.");
          ev.preventDefault();
          return;
        }
        this.currentStep = 1;
        ev.preventDefault();
      } else if (this.currentStep === 1) {
        if (this.activeMappings.length === 0) {
          alert("Bitte wählen Sie mindestens eine Spalte für den Import aus.");
          ev.preventDefault();
          return;
        }
        this.currentStep = 2;
        ev.preventDefault();
      } else if (this.currentStep === 2) {
        this.performImport();
        ev.preventDefault();
      } else if (this.currentStep === 3) {
        this.$refs.excelimportmodal.hide();
      }
    },

    handleBack(ev) {
      if (this.currentStep === 3) {
        this.resetModal();
        ev.preventDefault();
      } else if (this.currentStep > 0) {
        this.currentStep--;
        ev.preventDefault();
      }
    },

    async performImport() {
      if (!this.projectId) {
        this.$bvToast.toast("Kein Projekt ausgewählt", {
          title: "Fehler",
          variant: "danger",
          autoHideDelay: 3000,
        });
        return;
      }

      this.isImporting = true; // Set loading state to true
      try {
        // Single call to execute import with mapping
        const importResponse = await this.axios.post(
          `${this.base_url}/projects/${this.projectId}/excel-import/execute`,
          {
            data: this.previewData,
            original_filename: (this.excelFile && this.excelFile.name) || 'unknown',
            headers: this.headers,
            mapping: this.mapping,
            zieldokumentfeld: this.zieldokumentfeld,
            assignment_behavior: this.assignmentBehavior
          }
        );

        this.importResults = importResponse.data;
        this.currentStep = 3; // Move to results step
      } catch (error) {
        console.error("Import failed:", error);
        const errorMessage = error.response && error.response.data && error.response.data.detail 
          ? error.response.data.detail 
          : error.message;
        this.$bvToast.toast("Import fehlgeschlagen: " + errorMessage, {
          title: "Fehler",
          variant: "danger",
          autoHideDelay: 3000,
        });
      } finally {
        this.isImporting = false; // Reset loading state
      }
    },

    resetModal() {
      this.currentStep = 0;
      this.excelFile = null;
      this.headers = [];
      this.previewData = [];
      this.mapping = [];
      this.importResults = null;
      this.zieldokumentfeld = "XLS"; // Reset to default value
      this.skipRows = 0;
      this.rawData = [];
      this.assignmentBehavior = "create"; // Reset to default value
      this.isImporting = false; // Reset import loading state
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    open() {
      if (!this.projectId) {
        this.$bvToast.toast("Bitte wählen Sie zuerst ein Projekt aus", {
          title: "Fehler",
          variant: "danger",
          autoHideDelay: 3000,
        });
        return;
      }
      this.$refs.excelimportmodal.show();
    }
  }
}
</script>

<style lang="scss" scoped>
.excel-import-modal {
  :deep(.modal) {
    .modal-dialog {
      max-height: 85vh;
      margin: 2.5vh auto;
      overflow: hidden;
    }
    
    .modal-content {
      max-height: 85vh;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }
    
    .modal-header {
      flex-shrink: 0;
    }
    
    .modal-body {
      overflow-y: auto;
      flex: 1;
      min-height: 0; // Important for flex child scrolling
    }
    
    .modal-footer {
      flex-shrink: 0;
    }
  }

  .table-responsive {
    max-height: calc(85vh - 200px); // Account for modal header, footer and other content
    overflow-y: auto;
    margin-bottom: 1rem;
    
    table {
      margin-bottom: 0;
    }
  }

  // Add specific styling for the data preview table
  .mt-3 .table-responsive {
    max-height: 400px; // Set a fixed max height for the preview table
    overflow-y: auto;
    
    table {
      margin-bottom: 0;
    }
  }

  .custom-file-upload {
    position: relative;
    
    .upload-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(255, 255, 255, 0.9);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1;
    }
  }
}
</style> 